import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { SectionHead } from '@/components/aurora'
import { FileText, ArrowRight } from 'lucide-react'
import { getMyStats } from '@/api/social'

function toneFor(pct) {
  if (pct >= 85) return 'var(--mint, #34d399)'
  if (pct >= 70) return 'var(--butter, #fbbf24)'
  return 'var(--coral)'
}

export default function RecentResults() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    getMyStats({ signal: controller.signal })
      .then(({ data }) => setResults(data.recent_results ?? []))
      .catch((err) => { if (err.code !== 'ERR_CANCELED') setResults([]) })
      .finally(() => { if (!controller.signal.aborted) setLoading(false) })
    return () => controller.abort()
  }, [])

  return (
    <div className="rounded-2xl border px-5 py-5" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
      <SectionHead
        title={t('dashboard.recentResults', "So'nggi natijalar")}
        more={
          <a
            href="/results"
            className="inline-flex items-center gap-1 text-[12px] transition-colors"
            style={{ color: 'var(--ink-3)', textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--ink)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-3)'}
          >
            {t('dashboard.seeAll', "Hammasi")} <ArrowRight size={12}/>
          </a>
        }
      />
      <div className="flex flex-col gap-2 mt-2">
        {!loading && results.length === 0 && (
          <p className="text-[12.5px] m-0" style={{ color: 'var(--ink-3)' }}>
            {t('stats.empty', "Hali imtihon ishlanmagan — birinchisini boshlang!")}
          </p>
        )}
        {results.slice(0, 4).map((row) => {
          const tone = toneFor(row.percent)
          return (
            <button
              key={row.result_id}
              onClick={() => navigate(`/exam/${row.exam_code}/result`)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left"
              style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}
            >
              <span
                className="w-9 h-9 rounded-full grid place-items-center flex-shrink-0 border"
                style={{ background: 'var(--coral-tint)', borderColor: 'var(--coral-soft)', color: 'var(--coral)' }}
              >
                <FileText size={16} strokeWidth={2}/>
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[13.5px] truncate m-0" style={{ color: 'var(--ink)' }}>{row.exam_title}</p>
                <p className="font-mono text-[10.5px] m-0 mt-0.5" style={{ color: 'var(--ink-3)' }}>
                  {row.finished_at ? new Date(row.finished_at).toLocaleDateString() : ''}
                </p>
              </div>
              <span className="font-mono text-[12px] font-bold flex-shrink-0" style={{ color: tone }}>
                {row.score}/{row.max_score}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
